import { useState, useEffect, useRef } from 'react';
import type { User } from 'firebase/auth';
import { AuthManager, AuthState } from '@/contexts/AuthManager';

/**
 * Simplified useAuth hook that uses centralized AuthManager
 * Replaces the complex multi-listener implementation
 */
export function useAuth(): AuthState & {
  clearCacheAndLogout: () => Promise<void>;
  refreshClaims: (firebaseUser?: User | null) => Promise<boolean>;
} {
  const [authState, setAuthState] = useState<AuthState>(() => 
    AuthManager.getInstance().getCurrentState()
  );
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const authManager = AuthManager.getInstance();
    
    // Subscribe to auth state changes from the centralized manager
    unsubscribeRef.current = authManager.subscribe(setAuthState);

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  // Expose logout function for components that need it
  const clearCacheAndLogout = async () => {
    const authManager = AuthManager.getInstance();
    await authManager.performLogout();
  };

  // Force-refresh the ID token + re-derive role-bearing state from fresh custom
  // claims (e.g. after the invite funnel stamps the `raise_owner` claim). Returns
  // the re-derived `isRaiseOwner` so callers can gate on the confirmed role; accepts
  // an optional Firebase user for the window before the auth observer has populated
  // state (a brand-new account).
  const refreshClaims = async (firebaseUser?: User | null): Promise<boolean> => {
    const authManager = AuthManager.getInstance();
    return authManager.refreshClaims(firebaseUser);
  };

  return {
    ...authState,
    clearCacheAndLogout,
    refreshClaims,
  };
} 